

import { Button } from '@repo/ui/components/ui/button';
import { useRouter } from '@repo/ui/hooks/use-router';
import { ArrowLeft, Download, Printer } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useInventoryData } from '../../../../hooks/use-inventory-data';
import { usePrintData } from '../../hooks/use-print-data';
import InterventionForm from './components/InterventionForm';
import { usePDFDownload } from '../../print/hooks/use-pdf-download';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { pdfStyles } from '../../print/shared';
import { getDynamicFieldOptions } from '../../shared/components/dynamic-field-options';
import { ARBRE_FIELDS_PRINT_CONFIG } from '@repo/field-configs';
import { formatDate } from '../../epaysage/print/utils/format-utils';
import { imageService } from '@repo/ui/lib/services';
import { useUserSettings } from '../../../../hooks/api/user-settings';

interface InterventionArbreProps {
  inventaireId: number;
}

export default function InterventionArbre({ inventaireId }: InterventionArbreProps) {
  // #region agent log

  // #endregion
  const router = useRouter();
  // #region agent log

  // #endregion
  const { especes, champignons, nuisibles } = useInventoryData(false);
  // #region agent log

  // #endregion
  const { data: userSettingsData } = useUserSettings();
  // #region agent log

  // #endregion
  const [printMode, setPrintMode] = useState(false);

  // Fetch print data from API (formatted with all replacements) - SINGLE SOURCE OF TRUTH
  const { data: printData, isLoading, error } = usePrintData(inventaireId);

  // Load inventory data when component mounts
  useEffect(() => {
    if (especes.length === 0 || champignons.length === 0 || nuisibles.length === 0) {
      // Trigger data loading if not already loaded
      // This will be handled by the useInventoryData hook
    }
  }, [especes, champignons, nuisibles]);

  const handlePrint = () => {
    setPrintMode(true);

    // Ensure all content is loaded and visible
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        // Force scroll to top to ensure all content is rendered
        window.scrollTo(0, 0);

        // Additional delay to ensure all content is rendered
        setTimeout(() => {
          window.print();
          setPrintMode(false);
        }, 200);
      } else {
        setPrintMode(false);
      }
    }, 300);
  };

  const handleBack = () => {
    router.back();
  };

  // Values from print-data API are already formatted strings, just return them as-is
  const getDynamicFieldValue = (field: string, value: any) => {
    if (!value) return "";
    return typeof value === 'string' ? value : String(value);
  };

  // Generate PDF function - creates intervention PDF using printData (same layout as InterventionForm)
  const generatePDF = useMemo(() => {
    return async (): Promise<Blob> => {
      if (!printData) {
        throw new Error('Données non disponibles pour la génération du PDF');
      }

      try {
        const { parsePrintDataGps, generateInterventionStaticMaps } = await import(
          './generate-static-maps'
        );
        const coord = parsePrintDataGps(printData.gps);
        const staticMapsBase64 = coord
          ? await generateInterventionStaticMaps(coord)
          : null;

        // Import the PDF generation function
        const { generateInterventionPDFDocument } = await import('./generate-intervention-pdf');

        // Create PDF document using intervention-specific layout
        const PDFDocumentComponent = generateInterventionPDFDocument({
          printData,
          inventaireId,
          userSettingsData,
          staticMapsBase64: staticMapsBase64 || undefined,
        });

        const blob = await pdf(PDFDocumentComponent).toBlob();

        if (!blob) {
          throw new Error('Impossible de générer le blob PDF');
        }

        return blob;
      } catch (error) {
        console.error('Erreur lors de la génération du PDF:', error);
        throw error instanceof Error ? error : new Error('Erreur inconnue lors de la génération du PDF');
      }
    };
  }, [printData, userSettingsData, inventaireId]);

  const { isGeneratingPDF, handleDownloadPDF } = usePDFDownload(generatePDF, {
    filename: `intervention-${inventaireId || 'fiche'}.pdf`,
    onError: (error) => {
      console.error('Erreur génération PDF:', error);
      alert(`Erreur lors de la génération du PDF: ${error.message}`);
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (error || !printData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erreur</h1>
          <p className="text-muted-foreground mb-4">
            Impossible de charger les données de l'inventaire
          </p>
          <Button onClick={handleBack} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white ${printMode ? 'print-mode' : 'min-h-screen'}`}>
      {/* Print Controls - Hidden when printing */}
      <div className="fixed top-4 right-4 print:hidden z-50 flex gap-2">

        <Button onClick={handleDownloadPDF} disabled={isGeneratingPDF || !printData} className="gap-2">
          <Download className="h-4 w-4" />
          {isGeneratingPDF ? 'Génération...' : 'Télécharger PDF'}
        </Button>
      </div>

      {/* Main Intervention Content */}
      <InterventionForm printData={printData} inventaireId={inventaireId} />


    </div>
  );
}
